import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useGetExternalIntegrationsQuery, useServiceJobFilter } from '../../../../../queries';
import { styleSheet as dbStyleSheet } from '../../../../pages/DataBoards/Home/styles';
import { white } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { AddBoardItemButton } from '../../../boards/AddBoardItemButton';
import { dataGridStyles } from '../../../DataGrid/data-grid-styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ToolsIcon } from '../../../svgs/icons/ToolsIcon';
import { EditServiceJobModal } from '../EditServiceJobModal';
import { SERVICE_JOB_PROVIDERS, formatServiceJobCurrency, formatServiceJobDate } from '../utils';
import { styleSheet } from './styles';

export const EntityServiceJobs = ({ entity }: { entity: Api.IEntity }) => {
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({ impersonationContext: undefined });
	const hasServiceJobIntegration = externalIntegrations?.some(
		integration => integration.enabled && SERVICE_JOB_PROVIDERS.includes(integration.provider)
	);
	const canEdit = !hasServiceJobIntegration;

	const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);

	const jobFilterQuery = useServiceJobFilter({
		enabled: !!entity.id,
		filterRequest: {
			criteria: [
				{
					property: Api.ServiceJobFilterProperty.ContactId,
					value: entity.id ?? '',
				},
			],
		},
	});

	const jobs = React.useMemo(() => {
		return jobFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [jobFilterQuery.data]);

	const addItemClicked = () => {
		setIsAddModalOpen(true);
	};

	// EditServiceJobModal invalidates the service-job queries on save, so react-query refetches
	// this list automatically — closing the modal is all that's needed here.
	const closeAddModal = () => {
		setIsAddModalOpen(false);
	};

	const newJob: Partial<Api.IServiceJob> = {
		contact: entity as Api.IProjectedContact,
		contactId: entity.id,
	};

	if (jobFilterQuery.isPending) {
		return <LoadingSpinner />;
	}

	return (
		<div>
			<div className={css(styleSheet.contentSection)}>
				<header className={css(styleSheet.contentHeader)}>
					<div className={css(dbStyleSheet.boardIconContainer, baseStyleSheet.bgBrightBlue)}>
						<ToolsIcon fill={white} className={css(styleSheet.serviceJobIcon)} />
					</div>
					<h6 className={css(styleSheet.contentListTitle)}>Service Jobs</h6>
					{canEdit ? (
						<AddBoardItemButton className={css(styleSheet.contentAddButton)} onClick={addItemClicked}>
							Add Service Job
						</AddBoardItemButton>
					) : null}
				</header>

				{!jobs.length ? <p className={css(styleSheet.emptyText)}>No service jobs exist for this contact.</p> : null}

				{jobs.length ? (
					<>
						<table className={css(dataGridStyles.table, styleSheet.tableOverrides)}>
							<tbody>
								{jobs.map((job, index) => {
									const rowStyles = index === 0 ? dataGridStyles.tableDataCellBorderless : dataGridStyles.tableDataCell;

									return (
										<tr key={`service-job-${job.id}`}>
											<td className={css(rowStyles)}>
												<span className={css(styleSheet.jobNumber)}>{job.jobNumber}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{job.summary || '-'}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{job.jobStatus?.name || '-'}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{formatServiceJobCurrency(job.total)}</span>
											</td>
											<td className={css(rowStyles)}>
												<span>{formatServiceJobDate(job.completedOn)}</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
						{jobFilterQuery.hasNextPage && !jobFilterQuery.isFetchingNextPage ? (
							<Waypoint bottomOffset='-200px' onEnter={() => jobFilterQuery.fetchNextPage()} />
						) : null}
					</>
				) : null}
			</div>

			{isAddModalOpen ? (
				<EditServiceJobModal
					key={newJob.id ?? 'new'}
					initialJob={newJob}
					isOpen={isAddModalOpen}
					setIsOpen={setIsAddModalOpen}
					onSave={closeAddModal}
					onCancel={closeAddModal}
				/>
			) : null}
		</div>
	);
};
